// @flow
import React from "react"
import { compose, withStateHandlers, type HOC } from "recompose"
import Page from "pages/_Page"
import SectionFooter from "components/SectionFooter"
import InventorySelector from "./InventorySelector"
import { AffiliateOnboardingWrap, StepWrapper, Steps, StepBack } from "./styled"
import { TermsAndConditions } from "./TermsAndConditions"
import AffiliateSelector from "./AffiliateSelector"
import Confirm from "./Confirm"
import { query } from "global/apolloClient/helpers"
import { getDetail } from "data/locations/queries"
import Next from "./Next"
import { Text } from "componentsStyled/Typography/Texts"
import { PageTitle } from "componentsStyled/Typography/Titles"
import Alert from "forms/Alert"
import StyledLink from "components/StyledLink"

const AffiliateRegistrationPage = ({
  data,
  setInventory,
  inventory,
  affiliate,
  fetchPlaceDetails,
  setAffiliateEmail,
  affiliateEmail,
  setVerificationCode,
  verificationCode,
  currentStep,
  totalSteps,
  incrementStep,
  decrementStep,
  valid,
  validateInventory,
  validateAffiliate,
  acceptedTOS,
  setTermsOfService,
  errorMessage,
  loading,
  setLoading,
}) => (
  <Page>
    <AffiliateOnboardingWrap>
      <PageTitle> Welcome To AwayCo </PageTitle>
      <Steps >
        <Text bigOnAllDisplays uppercase bold>
          Step {currentStep} of {totalSteps}
        </Text>
      </Steps>
      {currentStep !== 1 && (<StepBack onClick={decrementStep}> Back </StepBack>)}
      {currentStep !== totalSteps && (
      <Next
        loading={loading}
        incrementStep={incrementStep}
        disabled={!valid}
      />)}
      {currentStep === 1 && (
        <StepWrapper>
          <Text bigOnAllDisplays uppercase bold>
            Lets get you online
          </Text>
          <Text bigOnAllDisplays uppercase bold>
            Firstly, lets find your store
          </Text>
          <br />
          <AffiliateSelector
            selectAffiliate={fetchPlaceDetails}
            affiliate={affiliate}
            setAffiliateEmail={setAffiliateEmail}
            validateAffiliate={validateAffiliate}
          />
          <Alert>{errorMessage}</Alert>
        </StepWrapper>
      )}
      {currentStep === 2 && (
        <StepWrapper>
          <Text bigOnAllDisplays uppercase bold>
            Choose some items you already have in inventory
          </Text>
          <Text>
            Dont worry, you can always change this later
          </Text>
          <br/>
          <Text> At the moment Awayco is only supporting a few outdoor sporting categories. We would like to serve more types of rental businesses over time. If you’d like to create an Awayco page for a different type of rental business, <StyledLink to="mailto:support@awayco.com">email us</StyledLink> </Text>
          <br />
          <Text> Please select the category of the inventory you have in stock to see the list of available models. If we don’t have certain model/s that you stock, please <StyledLink to="mailto:support@awayco.com">email us</StyledLink> the model specs and a photo and we can add it for you. </Text>
          <br />
          <Text bold> Click on the model you have in stock and select your sizing or variant to add it to your Awayco rental page </Text>
          <InventorySelector inventory={inventory}
           setInventory={setInventory}
           validateInventory={validateInventory} />
        </StepWrapper>
      )}
      {currentStep === 3 && (
        <StepWrapper>
          <TermsAndConditions accepted={acceptedTOS} setAccepted={setTermsOfService} />
        </StepWrapper>
      )}
      {currentStep === 4 && (
        <StepWrapper>
          <Text bigOnAllDisplays uppercase bold>
            We need to verify that you are a store
          </Text>
          <Text>
            You’ll receive call on the phone listed with Google, please enter the code that is read out or texted to you in the text box below
          </Text>
          <Confirm
            loading={loading}
            setLoading={setLoading}
            inventory={inventory}
            affiliate={affiliate}
            affiliateEmail={affiliateEmail}
            verificationCode={verificationCode}
            setVerificationCode={setVerificationCode}/>
        </StepWrapper>
      )}
    </AffiliateOnboardingWrap>
    <SectionFooter />
  </Page>
)

type Outter = {|
  location: { search: string },
|}

const validateAffiliateEmail = (email: string): boolean => {
  // Source: http://jsfiddle.net/ghvj4gy9/embedded/result,js/
  var emailTest = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return emailTest.test(email.toLocaleLowerCase())
}

const mandatoryAffiliateFields = {
  affiliateName: "Store name",
  formattedAddress: "Address",
  formattedOpeningHours: "Opening hours",
  formattedPhoneNumber: "Phone number"
}

const enhancer: HOC<*, Outter> = compose(
  withStateHandlers(
    {
      affiliate: undefined,
      valid: false,
      errorMessage: "",
      loading: false
    },
    {
      setValid: () => v => ({ valid: v }),
      setAffiliate: () => v => ({ affiliate: v }),
      setLoading: () => v => ({ loading: v }),
      validateInventory: (state, props) => inventory => {
        return {
          valid: inventory.length > 0,
        }
      },
      validateAffiliate: (state, props) => () => {
        const affiliate = state.affiliate
        const missingFields = []
        for (const key in mandatoryAffiliateFields) {
          if (
            affiliate &&
            affiliate.hasOwnProperty(key) &&
            (affiliate[key] === null || affiliate[key] === undefined)
          ) {
            missingFields.push(mandatoryAffiliateFields[key])
          }
        }
        const validEmail = affiliate && validateAffiliateEmail(affiliate.affiliateEmail)
        const errorMessage = missingFields.length > 0 ? `Awayco is only supporting self-onboarding by existing Stores at this time, please choose a Store location to get started. We require the following additional fields from you: ${missingFields.toLocaleString()}.` : undefined
        const valid = affiliate !== undefined && errorMessage === undefined && validEmail
        return {
          valid: valid,
          errorMessage: errorMessage,
        }
      },
    },
  ),
  withStateHandlers(
    {
      inventory: [],
      affiliateEmail: "",
      verificationCode: "",
      acceptedTOS: false,
      currentStep: 1,
      totalSteps: 4,
      //HACK(HARRY) needed to async await fetch place details execution
      //TODO(ray>harry): I don't think that recompose will wait for the async
      //execution to complete anyway so just make the function non-async and
      //instead of using `await`, use `void` which indicates that it will go
      //off and do its own thing. Then you can remove these hax.
      catch: undefined,
      then: undefined,
      finally: undefined,
    },
    {
      // TODO(HARRY) pass in a generated token to link api calls with for billing purposes
      fetchPlaceDetails: (state, props) => async (placeId) => {
        props.setLoading(true)
        const affiliateDetails = await query(getDetail, {
          placeId: placeId,
        })
        props.setLoading(false)
        props.setAffiliate(affiliateDetails.place)
        props.validateAffiliate()
      },
      setInventory: (state, props) => inventory => {
        props.validateInventory(inventory)
        return { inventory: inventory }
      },
      setAffiliateEmail: (state, props) => affiliateEmail => {
        props.setAffiliate({
          ...props.affiliate,
          affiliateEmail: affiliateEmail,
        })
        props.validateAffiliate()
        return { affiliateEmail: affiliateEmail }
      },
      setVerificationCode: props => verificationCode => {
        return { verificationCode }
      },
      setTermsOfService: (state, props) => acceptedTOS => {
        props.setValid(acceptedTOS)
        return { acceptedTOS }
      },
      incrementStep: (state, props) => () => {
        if (state.currentStep < state.totalSteps) {
          window.scrollTo(0,0)
          return {
            currentStep: 1 + state.currentStep,
          }
        }
      },
      decrementStep: (state, props) => () => {
        window.scrollTo(0,0)
        if (state.currentStep > 1) {
          return {
            currentStep: state.currentStep - 1,
          }
        }
      },
    },
  ),
)

export default enhancer(AffiliateRegistrationPage)



// WEBPACK FOOTER //
// ./src/pages/AffiliateRegistration/index.js